/**
 * @module app.shared
 */

import { processContent, observable, BindingEngine, autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { IAccordion } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { Subscription } from 'aurelia-event-aggregator';
import { EssCheckboxCustomElement, CheckboxState } from 'app/shared/elements/forms/ess-checkbox';
import { EssContextMenuCustomElement } from 'app/shared/elements/ess-context-menu';
import { getComponentViewModel } from 'app/shared/lib/aurelia';
import { SortableCustomAttribute } from 'app/shared/attributes/sortable-attribute';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { Router } from 'aurelia-router';
import { TableConfigurationService, TableConfiguration, ITableConfiguration } from 'app/shared/services/table-configuration-service';
import { animateOnce, reorderElements } from 'app/shared/lib/dom';
import { getAttributeBinding } from 'app/shared/lib/binding';
import { debounce, microTask } from 'app/shared/lib/decorators';
import { bindable } from 'aurelia-typed-observable-plugin';
import { AccountContext, AccountPermissions } from 'app/core/lib/account-context';
import { getQuadrantCoordinates } from 'app/shared/lib/screen';
import { Toaster } from 'app/shared/lib/toaster';
import { removeTranslationKeyQualifiers } from 'app/shared/lib/i18n';
import { I18N } from 'aurelia-i18n';
import { getKey, Key } from 'app/shared/lib/keys';
import { MatcherFunction, modelIdMatcher } from 'app/shared/lib/matcher';
import { resolveProperty } from 'app/shared/lib/objects';
import { guid } from 'app/shared/lib/guid';
import { getFocusSelector, IReturnFocus, returnFocus } from 'app/shared/lib/focus';
import { SortDirection, Sorting } from 'app/shared/lib/essette-site';

// INFO [Matt] This RegExp is for attempting to find a property name out of a template.
const INTERPOLATION_RE = /^\s*\${((?:[A-Za-z0-9]+)(?:\.[A-Za-z0-9]+)*)(?:\s*\|\s*[a-zA-Z0-9:]*)*}\s*$/;
const HEADER_ATTRIBUTE = 'header';
const KEY_ATTRIBUTE = 'key';
const FIELD_ATTRIBUTE = 'field';
const DEFAULT_ATTRIBUTE = 'default';
const TEST_NAME_ATTRIBUTE = 'test-name';
const COL_HEADER_HIDDEN_ATTRIBUTE = 'col-header-hidden';
const HEADER_ATTRIBUTE_BLACKLIST = [/ess-row-class/i];
const CELL_ATTRIBUTE_BLACKLIST = [/sortable/i];
const HEADER_SLOT = 'header-template';
const ROW_SLOT = 'row-template';
const CONFIRM_KEYS = [
    Key.Enter,
    Key.Space
]

function getKeyAttribute(col: Element): string | null {
    let key = col.getAttribute(KEY_ATTRIBUTE);
    if(key) {
        const match = key.match(INTERPOLATION_RE);

        // key="${value}" => ${value | t}
        if(match) {
            return match[1];
        }

        // key="cm.case.$" => ${'cm.case.$' | t}
        else {
            return `'${key}'`;
        }
    }

    // key.bind="value" => ${value | t}
    else {
        for(let binding of ['bind']) {
            key = col.getAttribute(`${KEY_ATTRIBUTE}.${binding}`);
            if(key) {
                return key;
            }
        }
    }

    return null;
}

function getBooleanAttribute(col: Element, attributeName: string): boolean {
    let attrVal = col.getAttribute(attributeName);
    return (attrVal === '' || attrVal === 'true');
}

function getOrderFromRow(row: Element): { elements: Element[], order: string[], indexes: number[] } {
    const elements = [];
    const order = [];
    const indexes = [];
    let index = 0;
    for(let el of row.children) {
        const key = el.getAttribute(KEY_ATTRIBUTE) || Reflect.get(el, KEY_ATTRIBUTE);
        if(key) {
            elements.push(el);
            order.push(key);
            indexes.push(index);
        }
        index += 1;
    }
    return { elements, order, indexes };
}

function camelCase(text: string) {
    return `${text.substr(0, 1).toLowerCase()}${text.substr(1)}`;
}

interface ISelectEvent<T> { item?: T, items: Array<T> }
export type SelectEvent<T> = CustomEvent<ISelectEvent<T>>;

interface IExpandEvent<T> { item?: T, items: Array<T> }
export type ExpandEvent<T> = CustomEvent<IExpandEvent<T>>;

// TODO [Matt] Pull interfaces into a separate file and use where applicable.
export interface ISelect<T> {
    selected: Array<T>;
    select(item: T): void;
    select(index: number): void;
    deselect(item: T): void;
    deselect(index: number): void;
    toggleSelected(item: T): void;
    toggleSelected(index: number): void;
    isSelected(item: T): void;
    isSelected(index: number): void;
}

// TODO [Matt] Merge with collapse custom attribute ICollapse.
export interface IExpand<T> extends IAccordion {
    expanded: Array<T>;
    open(item: T): void;
    open(index: number): void;
    openAll(): void;
    openAll(): void;
    close(item: T): void;
    close(index: number): void;
    closeAll(): void;
    closeAll(): void;
    toggleExpanded(item: T): void;
    toggleExpanded(index: number): void;
    isExpanded(item: T): boolean;
    isExpanded(index: number): boolean;
}

interface IDragContainer {
    y0: number,
    element: HTMLElement,
    breakpointMap: Map<HTMLElement, { index: number, breakpoint: number }>
}

const TableClass = {
    ColumnHeadingText: 'ess-col-heading-text',
    DetailTable: 'ess-detail-table',
    DetailRow: 'ess-detail-row',
    DraggableList: 'ess-draggable-list',
    DraggableListItem: 'ess-draggable-list-item',
    HiddenColumn: 'ess-col-hidden',
    ExpandedDetailRow: 'ess-expanded'
}

const TableSelector = {
    DetailTable: `.${TableClass.DetailTable}`,
    DetailRow: `.${TableClass.DetailRow}`,
    BodyRows: `tr:not(.${TableClass.DetailRow})`,
    Sortable: '[sortable]',
    DraggableList: `.${TableClass.DraggableList}`,
    DraggableListItem: `.${TableClass.DraggableListItem}`
}

@autoinject()
@processContent(EssTableCustomElement.processContent)
export class EssTableCustomElement<T> implements ISelect<T>, IExpand<T> {

    // table data, assumed an array of rows
    @bindable.array() data: Array<T> = [];

    // table title for accessability
    @bindable.stringNull() captionTitle: string | null = null;
    @bindable.stringNull() describedBy: string | null = null;

    // whether or not to show the checkboxes, default false
    @bindable.booleanAttr() selectable = false;
    @bindable.stringNull() public selectableGroupName: string | null = null;
    @bindable.stringNull() public rowIdentifierProperty: string | null = null;
    @bindable.booleanAttr({ defaultBindingMode: bindingMode.oneTime }) paged = false;
    @bindable.booleanAttr() scrollable = true;

    @bindable.booleanAttr({ defaultBindingMode: bindingMode.oneTime }) configurable = false;

    // Optional tooltip to show for "Select All" tooltip
    @bindable.stringNull() selectAllTooltip: string | null = null;

    // INFO [Matt] Unique key for the table, used for loading table
    // configuration. Defaults to ${route-name}.${data-binding-name}.
    @bindable.string({ attribute: 'key' }) protected tableName!: string;

    @bindable.string() public testName: string = '';

    // allows for specifying the name of a property in the object array to which the table is bound
    // which will contain a boolean to indicate whether or not the selectable checkbox should be enabled
    @bindable.string() rowSelectable: string = '';

    // allows for specifying the name of a property in the object array to which the table is bound
    // which will contain a boolean to indicate whether or not the expand arrow should be visible
    @bindable.string() rowExpandable: string = '';

    // Specifies the matcher function used to restore selected and expanded lists, and many other uses
    // where logic requires matching two objects.
    @bindable.function
    public matcher: MatcherFunction = modelIdMatcher;

    @observable()
    public selected: Array<T> = [];
    public expanded: Array<T> = [];

    protected hasDetail = false;
    protected columnCount: number = 0;
    protected visibleColumns: string[] = [];
    // store changes to the visible columns here before they're actually applied to the table config
    protected configuredColumns: string[] = [];
    protected defaultConfig!: ITableConfiguration;
    protected adminConfiguration = false;
    protected isAdmin = false;
    protected drag: IDragContainer | null = null;
    protected tableConfig?: TableConfiguration;
    // store changes to the table config here before they're actually applied to the table config
    protected tempTableConfig?: TableConfiguration;
    protected defaultMatcher = modelIdMatcher;
    protected selectAllChecked: boolean | null = false;

    protected dataSubscription: Subscription | null = null;
    protected selectedSubscription: Subscription | null = null;
    protected tableHeaderObserver: MutationObserver;
    protected tableBodyObserver: MutationObserver;
    protected tableResizeObserver: ResizeObserver;
    protected activeElementPath: IReturnFocus | null = null;
    protected hadActiveElement: boolean = false;
    protected contextSortable: SortableCustomAttribute | null = null;
    private editingPositionColumnKey: string | null = null;
    protected id: guid = guid();
    protected static readonly i18n: I18N = new I18N();

    protected handleDocumentMousemove = (event: MouseEvent) => {
        if(this.drag !== null) {
            const { element, y0 } = this.drag;
            const dy = event.clientY - y0;
            element.style.transform = `translateY(${dy}px)`;
        }
    }

    protected handleMouseup = (event: MouseEvent) => {
        if(this.drag !== null) {
            const { element } = this.drag;
            animateOnce(this.drag.element, { transform: 'translateY(0px)' });
            element.style.zIndex = '';
            element.style.pointerEvents = '';
            this.drag = null;
            document.removeEventListener('mousemove', this.handleDocumentMousemove);
            document.removeEventListener('mouseup', this.handleMouseup);
        }
    }

    protected fireEventOn = fireEventOn;

    protected respondToTableHeaderMutations = () => {
        this.columnCount = this.$tableHeader.childElementCount;
        this.respondToTableMutations();
    };

    protected handleColumnConfigurationKeydown(event: KeyboardEvent, columnKey: string) {
        const columnName = this.getColumnDisplay(columnKey);
        const columns = Array.from(this.$columns.children as Iterable<HTMLElement>);
        const columnElement = columns.find((col) => col.contains(event.target as HTMLElement));
        if(!columnElement) { return true; }

        const editPositionMode = !!this.editingPositionColumnKey;
        const currentPosition = parseInt(columnElement.style.order);
        const columnCount = this.$columns.children.length;


        switch(getKey(event)) {
            case Key.Enter:
            case Key.Space:
                if(editPositionMode && columnKey === this.editingPositionColumnKey) {
                    // Confirm the position & Exit Edit Mode
                    this.editingPositionColumnKey = null;
                    columnElement.classList.remove('ess-style-accent');
                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.confirmed', { index: currentPosition + 1, length: columnCount, item: columnName });
                    this.updateLiveText(ariaMessage);
                    return false;
                }
                else {
                    // Enter Edit Mode
                    this.editingPositionColumnKey = columnKey;
                    columnElement.classList.add('ess-style-accent');
                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.enabled');
                    this.updateLiveText(ariaMessage);
                    return false;
                }
                return true;
            case Key.Up:
            case Key.Left:
                if(editPositionMode) {
                    const newPosition = Math.max(currentPosition - 1, 0);
                    const swapColumn = columns.find((col) => col.style.order === `${newPosition}`);
                    columnElement.style.order = `${newPosition}`
                    if(swapColumn) { swapColumn.style.order = `${currentPosition}`; }

                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.position', { index: newPosition + 1, length: columnCount });
                    this.updateLiveText(ariaMessage);
                    document.activeElement?.scrollIntoView({ block: "center", behavior: "auto" });
                    return false;
                }
                return true;
            case Key.Down:
            case Key.Right:
                if(editPositionMode) {
                    const newPosition = Math.min(currentPosition + 1, columnCount - 1);
                    const swapColumn = columns.find((col) => col.style.order === `${newPosition}`);
                    columnElement.style.order = `${newPosition}`;
                    if(swapColumn) { swapColumn.style.order = `${currentPosition}`; }

                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.position', { index: newPosition + 1, length: columnCount });
                    this.updateLiveText(ariaMessage);
                    document.activeElement?.scrollIntoView({ block: "center", behavior: "auto" });
                    return false
                }
                return true;
            case Key.Tab:
                if(editPositionMode) {
                    // Exit Edit Mode
                    this.editingPositionColumnKey = null;
                    columnElement.classList.remove('ess-style-accent');
                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.confirmed', { index: currentPosition + 1, length: columnCount, item: columnName });
                    this.updateLiveText(ariaMessage);
                }
                return true;
            case Key.Esc:
                if(editPositionMode) {
                    // Exit Edit Mode & undo changes
                    this.editingPositionColumnKey = null;
                    columnElement.classList.remove('ess-style-accent');
                    const ariaMessage = this.i18n.tr('ui:table.column.reorder.cancelled');
                    this.updateLiveText(ariaMessage);
                    this.resetOrder();
                    event.stopImmediatePropagation();
                    return false;
                }
                return true;
        }
        return true;
    }

    protected respondToTableMutations = () => {
        if(this.tableConfig?.order.length) {
            this.reorderColumns();
            this.updateVisibility();
        }
    }

    @debounce()
    protected respondToTableResize() {
        if(this.$table) {
            this.$table.classList.add('ess-processing');
            this.$table.style.setProperty('--ess-table-width', `0px`); //this.element.scrollWidth is dependent on --ess-table-width. Which creates a loop. This is to reset the calculation (see ess-detail-wrapper class)
            this.$table.style.setProperty('--ess-table-width', `${this.element.scrollWidth}px`);
            this.$table.classList.remove('ess-processing');
        }
    };

    protected respondToTableConfigUpdate = () => {
        this.reconcileColumns();
        this.reorderColumns();
        this.updateVisibility();
    };

    // respond to when a change is made in the table configuration menu (e.g., user clicks Reset)
    protected respondToTempTableConfigUpdate = () => {
        const { $tableHeader, $tableBody } = this;
        if(!this.tempTableConfig || !$tableHeader || !$tableBody) {
            return;
        }
        const { hidden } = this.tempTableConfig;
        const { elements: headerCells, order } = getOrderFromRow($tableHeader);
        const configuredColumns: string[] = [];
        headerCells.forEach((cell, index) => {
            const key = order[index];
            const visible = !hidden.includes(key);
            if(visible) {
                configuredColumns.push(key);
            }
        });
        this.configuredColumns = configuredColumns;

        this.resetOrder();
        this.resetEditModeStyleAttribute();
    };

    ////****************** Element References ***************************************////
    protected $table!: HTMLTableElement;
    protected $columns!: HTMLElement;
    protected selectAllElement!: EssCheckboxCustomElement | undefined;
    protected $tableHeader!: HTMLTableRowElement;
    protected $tableBody!: HTMLTableRowElement;
    protected $contextMenu!: EssContextMenuCustomElement;
    protected $configurationModal!: EssModalCustomElement;
    protected $liveMessage!: HTMLSpanElement;
    protected $contextMenuButton!: HTMLElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(public readonly element: Element, protected readonly bindingEngine: BindingEngine, protected readonly tableConfigurationService: TableConfigurationService,
        protected readonly account: AccountContext, protected readonly router: Router, protected readonly toaster: Toaster,
        protected readonly i18n: I18N, protected readonly taskQueue: TaskQueue) {
        this.tableHeaderObserver = new MutationObserver(this.respondToTableHeaderMutations);
        this.tableBodyObserver = new MutationObserver(this.respondToTableMutations);
        this.tableResizeObserver = new ResizeObserver(this.respondToTableResize.bind(this));
    }

    protected bind() {
        if(!this.tableName) {
            this.tableName = `${this.router.currentInstruction.config.name}.${this.element.getAttribute('data.bind')}`;
        }
        if(this.configurable) {
            this.tableConfig = this.tempTableConfig = this.tableConfigurationService.getTableConfiguration(this.tableName);
            this.isAdmin = this.account.hasPermission(AccountPermissions.ApplicationAdmin);
            this.loadTableConfiguration();
        }
    }

    protected attached() {
        this.hasDetail = this.element.matches(TableSelector.DetailTable);
        const { order, elements } = getOrderFromRow(this.$tableHeader);
        const hidden = elements.reduce((hidden, el, index) => {
            if(Reflect.get(el, DEFAULT_ATTRIBUTE) === false || el.getAttribute(DEFAULT_ATTRIBUTE) === 'false') {
                hidden.push(order[index])
            }
            return hidden;
        }, [] as string[]);
        this.defaultConfig = { order, hidden, locked: [] };
        if(this.configurable) {
            this.tableConfig?.addEventListener('update', this.respondToTableConfigUpdate);
            this.tempTableConfig?.addEventListener('tempupdate', this.respondToTempTableConfigUpdate);
            this.reconcileColumns();
        }
        this.respondToTableResize();
        this.respondToTableHeaderMutations();
        this.connectTableObservers();

        this.$tableHeader.addEventListener('keydown', this.handleKeyDown, false);
    }

    protected detached() {
        this.disconnectTableObservers();
        if(this.tableConfig) {
            this.tableConfig.removeEventListener('update', this.respondToTableConfigUpdate);
        }

        if(this.tempTableConfig) {
            this.tempTableConfig.removeEventListener('tempupdate', this.respondToTempTableConfigUpdate);
        }

        this.$tableHeader.removeEventListener('keydown', this.handleKeyDown);
    }

    // listen for keyboard interactions on clickable items
    protected handleKeyDown(event: KeyboardEvent): void {
        const element = event.target as HTMLElement;
        if(!event.defaultPrevented && this.taskQueue && CONFIRM_KEYS.includes(getKey(event) as Key)) {
            this.taskQueue.queueTask(() => element.click());
        }
    }

    protected getColumnDisplay(key: string): string | null {
        for(const header of Array.from(this.$tableHeader.querySelectorAll('[key]'))) {
            const headerKey = header.getAttribute(KEY_ATTRIBUTE) || Reflect.get(header, KEY_ATTRIBUTE);
            if(headerKey === key) {
                const innerElement = header.querySelector('.ess-col-heading-text');
                if(innerElement) {
                    return innerElement.textContent;
                }
            }
        }
        return this.i18n.tr(key);
    }

    protected getItemDisplay(item: any) {
        if(this.rowIdentifierProperty) {
            const display = resolveProperty(item, this.rowIdentifierProperty);
            if(typeof display === 'object') {
                return resolveProperty(item, this.rowIdentifierProperty + '.$display');
            } else {
                return display;
            }
        }
        else {
            return item?.$display;
        }
    }

    protected reconcileColumns() {
        if(!this.tableConfig) {
            return;
        }

        // INFO [Matt] Reconcile current columns with incoming list
        const { tableConfig } = this;
        const { order: currentOrder, elements } = getOrderFromRow(this.$tableHeader);

        // if this table hasn't yet been configured, initialize it based on the developer configuration
        if(!tableConfig.order.length) {
            Object.assign(this.tableConfig, this.defaultConfig);
        }

        // otherwise, if there are new developer defined keys not present in the user configuration, add them to the user configuration
        else if(tableConfig.order.some((key) => !currentOrder.includes(key)) || tableConfig.order.length !== currentOrder.length) {
            const newOrder: string[] = [];

            // for each key in the developer defined column list
            currentOrder.forEach((key, index) => {
                const element = elements[index];

                // if the user defined order doesn't have the key, add it
                if(!tableConfig.order.includes(key)) {
                    newOrder.push(key);
                }

                // otherwise, add the next item from the user defined order which is also in the developer defined order, if it exists
                else {
                    const next = tableConfig.order.find((key) => !newOrder.includes(key) && currentOrder.includes(key))
                    if(next) {
                        newOrder.push(next);
                    }
                }

                // and if the key is developer hidden, add it to the hidden list
                if(Reflect.get(element, DEFAULT_ATTRIBUTE) === false || element.getAttribute(DEFAULT_ATTRIBUTE) === 'false') {
                    tableConfig.hidden.push(key);
                }
            });
            this.tableConfigurationService.updateTableConfiguration(this.tableName, { order: newOrder });
        }
    }

    ////****************** Events ***************************************************////
    protected dataChanged() {
        this.queueReturnFocus();

        if(this.dataSubscription) {
            this.dataSubscription.dispose();
            this.dataSubscription = null;
        }
        if(Array.isArray(this.data)) {
            this.dataSubscription = this.bindingEngine.collectionObserver(this.data)
                .subscribe(() => {
                    this.syncSelectAllCheckbox();
                    this.syncExpandedList();
                });
            this.syncSelectAllCheckbox();
            this.syncExpandedList();
        }
    }

    protected selectedChanged() {
        if(this.selectedSubscription) {
            this.selectedSubscription.dispose();
            this.selectedSubscription = null;
        }
        if(Array.isArray(this.selected)) {
            this.selectedSubscription = this.bindingEngine.collectionObserver(this.selected)
                .subscribe(() => this.syncSelectAllCheckbox());
            this.syncSelectAllCheckbox();
        }
    }

    protected matcherChanged(matcher: MatcherFunction | null) {
        if(matcher === null) {
            this.matcher = this.defaultMatcher;
        }
        this.dataChanged();
    }

    ////****************** Functions ************************************************////
    protected connectTableObservers() {
        if(this.$table) {
            this.tableResizeObserver.observe(this.element);
            this.tableHeaderObserver.observe(this.$tableHeader, { childList: true });
            this.tableBodyObserver.observe(this.$tableBody, { childList: true });
        }
    }

    protected disconnectTableObservers() {
        this.tableHeaderObserver.disconnect();
        this.tableBodyObserver.disconnect();
        this.tableResizeObserver.disconnect();
    }

    // Expands a row by item or index.
    public open(item: T): void;
    public open(index: number): void;
    public open(itemOrIndex: T | number) {
        const opened = this.toggleInArray(itemOrIndex, this.expanded, true) === true;
        if(opened) {
            const detail: IExpandEvent<any> = { item: itemOrIndex, items: this.expanded };
            fireEventOn(this.element, 'expandchange', detail);
        }
    }

    // Expands all details.
    public openAll() {
        this.data.forEach((_, index) => {
            this.toggleInArray(index, this.expanded, true);
            const detail: IExpandEvent<any> = { items: this.expanded };
            fireEventOn(this.element, 'expandchange', detail);
        });
    }

    // Collapse a row by item or index.
    public close(item: T): void;
    public close(index: number): void;
    public close(itemOrIndex: T | number) {
        const closed = this.toggleInArray(itemOrIndex, this.expanded, false) === false;
        if(closed) {
            const detail: IExpandEvent<any> = { item: itemOrIndex, items: this.expanded };
            fireEventOn(this.element, 'expandchange', detail);
        }
    }

    // Collapses all rows.
    public closeAll() {
        this.data.forEach((_, index) => {
            this.toggleInArray(index, this.expanded, false);
            const detail: IExpandEvent<any> = { items: this.expanded };
            fireEventOn(this.element, 'expandchange', detail);
        });
    }

    // Close all rows except the one associated with an item or index.
    public closeOthers(item: T): void;
    public closeOthers(index: number): void;
    public closeOthers(itemOrIndex: T | number) {
        const { item, index } = this.findInArray(itemOrIndex, this.data);
        this.data.forEach((_, _index) => {
            if(index != _index) {
                this.toggleInArray(_index, this.expanded, false);
                const detail: IExpandEvent<any> = { items: this.expanded };
                fireEventOn(this.element, 'expandchange', detail);
            }
        });
    }

    // Toggle collapsed state of a row by item or index;
    public toggleExpanded(item: T): void;
    public toggleExpanded(index: number): void;
    public toggleExpanded(itemOrIndex: T | number) {
        const opened = this.toggleInArray(itemOrIndex, this.expanded);
        if(opened !== undefined) {
            const detail: IExpandEvent<any> = { item: itemOrIndex, items: this.expanded };
            fireEventOn(this.element, 'expandchange', detail);
        }
    }

    // Returns true if the row associated with an item or index is currently expanded.
    public isExpanded(item: T): boolean;
    public isExpanded(index: number): boolean;
    public isExpanded(itemOrIndex: T | number): boolean {
        const { item } = this.findInArray(itemOrIndex, this.expanded);
        return !!item && this.expanded.includes(item);
    }

    // Selects a row by item or index.
    public select(item: T): void;
    public select(index: number): void;
    public select(itemOrIndex: T | number) {
        const selected = this.toggleInArray(itemOrIndex, this.selected, true) === true;
        if(selected) {
            const detail: ISelectEvent<any> = { item: itemOrIndex, items: this.selected };
            fireEventOn(this.element, 'selectchange', detail);
        }
    }

    // Selects all rows.
    public selectAll() {
        this.data.forEach((_, index) => {
            this.toggleInArray(index, this.selected, true);
            const detail: ISelectEvent<any> = { items: this.selected };
            fireEventOn(this.element, 'selectchange', detail);
        });
    }

    // De-selects a row by item or index.
    public deselect(item: T): void;
    public deselect(index: number): void;
    public deselect(itemOrIndex: T | number) {
        const deselected = this.toggleInArray(itemOrIndex, this.selected, false) === false;
        if(deselected) {
            const detail: ISelectEvent<any> = { item: itemOrIndex, items: this.selected };
            fireEventOn(this.element, 'selectchange', detail);
        }
    }

    // De-selects all rows.
    public deselectAll() {
        this.data.forEach((_, index) => {
            this.toggleInArray(index, this.selected, false);
            const detail: ISelectEvent<any> = { items: this.selected };
            fireEventOn(this.element, 'selectchange', detail);
        });
    }

    // Toggle selected state of a row by item or index;
    public toggleSelected(item: T): void;
    public toggleSelected(index: number): void;
    public toggleSelected(itemOrIndex: T | number) {
        const selected = this.toggleInArray(itemOrIndex, this.selected);
        if(selected !== undefined) {
            const detail: ISelectEvent<any> = { item: itemOrIndex, items: this.selected };
            fireEventOn(this.element, 'selectchange', detail);
        }
    }

    // Returns true if the row associated with an item or index is currently selected.
    public isSelected(item: T): boolean;
    public isSelected(index: number): boolean;
    public isSelected(itemOrIndex: T | number): boolean {
        const { item } = this.findInArray(itemOrIndex, this.selected);
        return !!item && this.selected.includes(item);
    }

    // Apply custom sorting settings
    public applySorting(sorting: Sorting) {
        let columnHeaders = this.element.querySelectorAll('.ess-col-heading-text');

        columnHeaders.forEach((element) => {
            const sortableElement = this.findSortable(element);

            if(!!sortableElement) {
                const foundSortEntry = sorting.entries.find(sort => sort.reference === sortableElement?.property);
                if(foundSortEntry) {
                    if(foundSortEntry.direction === SortDirection.Ascending) {
                        sortableElement?.sortAscending();
                    }
                    else if(foundSortEntry.direction === SortDirection.Descending) {
                        sortableElement?.sortDescending();
                    }
                }
            }
        });
    }

    protected syncExpandedList(matcher: (a: T, b: T) => boolean = this.matcher) {
        this.expanded = this.expanded.reduce<any[]>((expanded, item) => {
            const match = this.data.find((data) => matcher(data, item));
            if(match) {
                expanded.push(match);
            }
            // INFO [Matt] If this is a paged table and the existing expanded
            // item doesn't have a match in the incoming data set, then we
            // probably navigated to a new page, so keep the item in the
            // expanded list. If this isn't a paged table, then this is likely
            // an old and irrelevant entry and can be discarded.
            else if(this.paged) {
                expanded.push(item);
            }
            return expanded;
        }, []);
    }

    protected syncSelectedList(matcher: (a: T, b: T) => boolean = this.matcher) {
        this.selected = this.selected.reduce<any[]>((selected, item) => {
            const match = this.data.find((data) => matcher(data, item));
            // If the selected item matches an item in the data, push it to the     selected items array
            if(match) {
                selected.push(match);
            }
            else {
                selected.push(item);
            }
            return selected;
        }, []);
    }

    protected syncSelectAllCheckbox() {
        // Filter data to only what is selectable
        const selectableData = this.rowSelectable == '' ? this.data : this.data.filter((d) => !!resolveProperty(d, this.rowSelectable));
        const intersection = this.data.filter((d) => this.selected.some((s) => this.matcher(d, s)));
        let checkedState: CheckboxState = CheckboxState.Indeterminate;

        if(intersection.length > 0 && selectableData.length === intersection.length) {
            checkedState = CheckboxState.Checked;
            this.selectAllChecked = true;
        } else if(intersection.length === 0) {
            checkedState = CheckboxState.Unchecked;
            this.selectAllChecked = false;
        } else {
            checkedState = CheckboxState.Indeterminate;
            this.selectAllChecked = null;
        }

        if(this.selectAllElement) {
            this.selectAllElement.state = checkedState;
        }
    }

    // Responds to changes in the select all checkbox
    // when the table is paged the default behavior doesn't solve the problem.
    protected handleSelectAllChecked(checked: boolean) {
        if(this.paged) {
            const existingSelection = this.selected.filter((s) => !this.data.some((d) => this.matcher(d, s)));
            if(checked) {
                // The filter logic here should stay in sync with item's checkbox disabled logic
                const selectedRows = !this.rowSelectable ? this.data : this.data.filter((d) => !!resolveProperty(d, this.rowSelectable));
                this.selected = existingSelection.concat(selectedRows);
            } else {
                this.selected = existingSelection;
            }
        } else {
            if(checked) {
                // The filter logic here should stay in sync with item's checkbox disabled logic
                const selectedRows = !this.rowSelectable ? this.data : this.data.filter((d) => !!resolveProperty(d, this.rowSelectable));
                this.selected.splice(0, Infinity, ...selectedRows);
            } else {
                this.selected.splice(0);
            }
        }

        // fire event
        fireEventOn(this.element, 'selectchange', { items: this.selected });
    }

    protected findInArray(itemOrIndex: T | number, array: Array<T>): { item: T, index: number } {
        let item: any;
        let index: number;
        if(typeof (itemOrIndex) === 'number') {
            item = array[itemOrIndex];
            index = itemOrIndex;
        } else {
            item = itemOrIndex;
            index = array.findIndex(elem => this.matcher(elem, item));
        }
        return { item, index };
    }

    protected toggleInArray(itemOrIndex: T | number, array: Array<T>, include?: boolean): boolean | undefined {
        const { index, item } = this.findInArray(itemOrIndex, this.data);
        if(!item || index === -1) {
            return;
        }
        const indexInArray = array.indexOf(item);
        if(indexInArray === -1) {
            if(include === true || include === undefined) {
                array.push(item);
                return true;
            }
        } else {
            if(include === false || include === undefined) {
                array.splice(indexInArray, 1);
                return false;
            }
        }
        return;
    }

    protected findSortable(element: Element) {
        const sortableElement = element.closest(TableSelector.Sortable);
        return sortableElement && getComponentViewModel<SortableCustomAttribute>(sortableElement, 'sortable');
    }

    protected queueReturnFocus() {
        //Track the active element path and if table had the active element prior to data refresh
        this.activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        this.hadActiveElement = this.$table.contains(document.activeElement);

        //If the table reloads and the focus is within, then focus the first element of the table
        this.taskQueue.queueTask(() => {
            if(this.hadActiveElement && this.activeElementPath && !this.$table.contains(document.activeElement)) {
                returnFocus(this.activeElementPath);
            }
            this.hadActiveElement = false;
        })
    }

    protected handleEllipsesClick(event: MouseEvent) {
        if(this.configurable === false) {
            return;
        }
        const target = event.target as Element;
        this.contextSortable = this.findSortable(target);
        const coords = getQuadrantCoordinates(target.getBoundingClientRect());
        this.$contextMenu.openAt(coords, this.$contextMenuButton);
        event.stopImmediatePropagation();
    }

    protected handleContextmenu({ target, clientX: x, clientY: y }: MouseEvent) {
        if(this.configurable === false) {
            return;
        }
        this.contextSortable = this.findSortable(target as Element);
        const coords = getQuadrantCoordinates({ x, y });
        this.$contextMenu.openAt(coords, this.$contextMenuButton);
    }

    @debounce()
    protected reorderColumns() {
        this.queueReturnFocus();

        if(!this.tableConfig) {
            return;
        }

        const newOrder = this.tableConfig.order;
        this.disconnectTableObservers();

        if(this.$table && this.$tableHeader && this.$tableBody) {
            let { elements, order: oldOrder } = getOrderFromRow(this.$tableHeader);
            reorderElements(elements, newOrder.map((key) => oldOrder.indexOf(key)));

            // find body rows (<tr> that aren't detail rows) that are direct children of this table's <tbody>
            const bodyRows = Array.from(this.$tableBody.querySelectorAll(TableSelector.BodyRows))
                .filter(r => r.parentElement === this.$tableBody);
            if(bodyRows.length > 0) {
                let { order: oldOrder, indexes } = getOrderFromRow(bodyRows[0]);
                const reorder = newOrder.map((key) => oldOrder.indexOf(key));
                bodyRows.forEach((row) => reorderElements(indexes.map((index) => row.children[index]), reorder));
            }
        }

        this.connectTableObservers();
    }

    @microTask
    protected resetOrder() {
        Array.from(this.$columns.children).forEach((el, i) => (<HTMLElement>el).style.order = `${i}`);
    }

    @microTask
    /**
     * Reconcile the column configuration's DOM order with the modified position order
     */
    protected reconcileConfigurationOrder() {
        let elements = Array.from(this.$columns.children);
        const order = elements
            .map(el => parseInt((el as HTMLElement).style.order))
            .map((_, index, order) => order.indexOf(index));
        reorderElements(elements, order);
    }

    protected resetEditModeStyleAttribute() {
        Array.from(this.$columns.children).forEach((el: Element, i: number) => {
            const column = el as HTMLElement;
            this.setEditModeStyleAttribute(column, false);
        });
    }

    protected setEditModeStyleAttribute(item: HTMLElement, editMode: boolean) {
        if(editMode) {
            item.classList.add('ess-style-accent');
            item.setAttribute('aria-selected', String(editMode));
        }
        else {
            item.classList.remove('ess-style-accent');
            item.setAttribute('aria-selected', String(editMode));
        }
    }

    protected cancelConfiguration() {
        this.loadTableConfiguration();
        this.respondToTempTableConfigUpdate();
        this.$configurationModal.close();
    }

    protected resetConfiguration() {
        if(!this.tempTableConfig) {
            return;
        }
        if(this.adminConfiguration) {
            Object.assign(this.tempTableConfig, this.defaultConfig);
            this.respondToTempTableConfigUpdate();
        }
        else {
            Object.assign(this.tempTableConfig, this.tableConfigurationService.revertTableConfiguration(this.tableName));
        }
    }

    protected applyConfiguration() {
        if(!this.tempTableConfig) {
            return;
        }
        const order = Array.from(this.$columns.children)
            .map(el => parseInt((el as HTMLElement).style.order))
            .map((_, index, order) => order.indexOf(index))
            .map((i) => this.tempTableConfig!.order![i]);
        const hidden = order.filter((key) => !this.configuredColumns.includes(key));
        const locked = this.tempTableConfig.locked;
        if(order.length === hidden.length) {
            this.toaster.warn().message({ key: 'ui:messages.warnings.minimumColumnsVisible' }).show();
        }
        else {
            if(this.adminConfiguration) {
                this.tableConfigurationService.updateTableAdminConfiguration(this.tableName, { order, hidden, locked });
                Object.assign(this.tempTableConfig!, this.tableConfigurationService.getAdminTableConfiguration(this.tableName));
                this.respondToTempTableConfigUpdate();
            } else {
                this.tableConfigurationService.updateTableConfiguration(this.tableName, { order, hidden });
            }
            this.closeAll();
            this.respondToTableMutations();
            this.$configurationModal.close();
        }
    }

    protected updateVisibility() {
        const { $tableHeader, $tableBody } = this;
        if(!this.tableConfig || !$tableHeader || !$tableBody) {
            return;
        }
        const { hidden } = this.tableConfig;
        const { elements: headerCells, order } = getOrderFromRow($tableHeader);
        const visibleColumns: string[] = [];
        headerCells.forEach((cell, index) => {
            const key = order[index];
            const visible = !hidden.includes(key);
            cell.classList.toggle(TableClass.HiddenColumn, !visible);
            if(visible) {
                visibleColumns.push(key);
            }
        });
        Array.from($tableBody.children).forEach((row) => {
            const { elements, order } = getOrderFromRow(row);
            elements.forEach((cell, index) => {
                cell.classList.toggle(TableClass.HiddenColumn, hidden.includes(order[index]) || false);
            });
        });
        this.visibleColumns = this.configuredColumns = visibleColumns;
    }

    protected handleMousedown(event: MouseEvent): true | void {
        if(this.drag) {
            return true;
        }

        const element = (event.target as Element).closest(TableSelector.DraggableListItem) as HTMLElement;
        this.drag = {
            element,
            y0: event.clientY,
            breakpointMap: this.buildBreakpointMap(element)
        }

        element.style.transition = '';
        element.style.zIndex = '1';
        element.style.pointerEvents = 'none';
        document.addEventListener('mousemove', this.handleDocumentMousemove);
        document.addEventListener('mouseup', this.handleMouseup);
    }

    protected handleMousemove(event: MouseEvent): true | void {
        if(this.drag === null) {
            return true;
        }

        const drag = this.drag;
        const { element, y0, breakpointMap } = drag;
        const dragIndex = parseInt(element.style.order);

        breakpointMap.forEach(({ breakpoint, index: itemIndex }, item) => {

            // drag down
            if(event.clientY > y0 && dragIndex < itemIndex && event.clientY > breakpoint) {

                // shift down
                drag.y0 += item.clientHeight;
                breakpoint -= item.clientHeight;
                item.style.order = `${itemIndex - 1}`;
                element.style.order = `${dragIndex + 1}`;
                breakpointMap.set(item, { index: itemIndex - 1, breakpoint: breakpoint - item.clientHeight });

            }

            // drag up
            else if(event.clientY < y0 && dragIndex > itemIndex && event.clientY < breakpoint) {

                // shift up
                drag.y0 -= item.clientHeight;
                item.style.order = `${itemIndex + 1}`;
                element.style.order = `${dragIndex - 1}`;
                breakpointMap.set(item, { index: itemIndex + 1, breakpoint: breakpoint + item.clientHeight });
            }
        });
    }

    protected updateLiveText(announcement: string) {
        // code to update text to our aria-live span
        this.$liveMessage.textContent = announcement;
    }

    protected buildBreakpointMap(element: Element): Map<HTMLElement, { index: number, breakpoint: number }> {
        const items = Array.from(element.closest(TableSelector.DraggableList)!.children as Iterable<HTMLElement>);
        return items.reduce((map, item) => {
            if(item !== element) {
                const index = parseInt(item.style.order);
                const { top, height } = item.getBoundingClientRect();
                map.set(item, { index, breakpoint: top + height / 2 });
            }
            return map;
        }, new Map());
    }

    protected sortAscending() {
        if(this.contextSortable !== null) {
            this.contextSortable.sortAscending();
        }
        this.closeContextMenu();
    }

    protected sortDescending() {
        if(this.contextSortable !== null) {
            this.contextSortable.sortDescending();
        }
        this.closeContextMenu();
    }

    protected closeContextMenu() {
        this.$contextMenu.close();
        this.contextSortable = null;
    }

    private loadTableConfiguration() {
        if(!this.tableConfig) {
            return;
        }
        // load the config appropriate to the view, then update the table
        if(this.adminConfiguration) {
            Object.assign(this.tableConfig, this.tableConfigurationService.getAdminTableConfiguration(this.tableName));
        } else {
            Object.assign(this.tableConfig, this.tableConfigurationService.getUserTableConfiguration(this.tableName));
        }
    }

    protected toggleAdminView() {
        if(!this.tableConfig) {
            return;
        }
        this.adminConfiguration = !this.adminConfiguration;
        this.loadTableConfiguration();
        this.respondToTableMutations();
    }

    protected openConfigurationModal() {
        this.$configurationModal.open(this.$contextMenuButton as HTMLElement);
        this.closeContextMenu();
        this.resetOrder();
        this.resetEditModeStyleAttribute();
    }

    // parses and transforms the incoming template to what is expected by aurelia
    protected static processContent(_compiler: any, _resources: any, el: Element, _instruction: any) {
        const cols = Array.from(el.children).filter(({ tagName }) => /ess-col/i.test(tagName));
        const details = Array.from(el.children).filter(({ tagName }) => /ess-detail/i.test(tagName));
        if(cols.length) {
            const row = document.createElement('template');
            const header = document.createElement('template');
            row.setAttribute('replace-part', ROW_SLOT);
            header.setAttribute('replace-part', HEADER_SLOT);

            cols.forEach((col, index) => {
                const key = getKeyAttribute(col);
                const rawKey = col.getAttribute(KEY_ATTRIBUTE);
                let name = getAttributeBinding(col, HEADER_ATTRIBUTE) || '';
                let colHeaderHidden = getBooleanAttribute(col, COL_HEADER_HIDDEN_ATTRIBUTE);

                // look for an explicit field property
                let field = col.getAttribute(FIELD_ATTRIBUTE);

                // if none present, try to infer from the template
                if(!field && col.childNodes.length === 1) {
                    const text = col.childNodes[0].textContent || '';
                    const match = text.match(INTERPOLATION_RE);
                    if(match) {
                        field = match[1];
                    }
                }

                // if still no match, and name is a property name
                if(!field && name.match(/^[a-zA-Z0-9]+$/)) {
                    field = camelCase(name);
                }

                if(!key)
                    // when header is empty, infer special case headers for the screen reader
                    if(!name) {
                        if(col.classList.contains('ess-col-actions')) {
                            name = "${'ui:labels.action' | label:'multiple' }";
                            colHeaderHidden = true;
                        } else if(col.classList.contains('ess-col-indicators')) {
                            name = "${'ui:labels.indicator' | label:'multiple' }";
                            colHeaderHidden = true;
                        }
                    } else if(col.classList.contains('ess-col-icons')) { //icons might have their own hidden header but not a (sortable) key
                        colHeaderHidden = true;
                    }

                // build the header and row template
                const outerHeading = document.createElement('th');
                const heading = document.createElement('div');
                const headingName = document.createElement('span');
                if(colHeaderHidden) {
                    headingName.classList.add('screen-reader-only');
                } else {
                    heading.classList.add('ess-inline-flex', TableClass.ColumnHeadingText);
                    // ensures the sorting control is not obfuscated for hidden header names
                }
                headingName.innerText = name || (key ? `\${${key} | t}` : '');
                heading.appendChild(headingName);

                const cell = document.createElement('td');
                Array.from(col.childNodes).forEach((node) => cell.appendChild(node));
                Array.from(col.attributes)
                    .filter(({ name }) => name !== HEADER_ATTRIBUTE)
                    .forEach(({ name, value }) => {
                        if(/sortable/i.test(name)) {
                            heading.setAttribute(name, value);
                        } else if(!HEADER_ATTRIBUTE_BLACKLIST.some((re) => re.test(name))) {
                            outerHeading.setAttribute(name, value);
                        }
                        if(!CELL_ATTRIBUTE_BLACKLIST.some((re) => re.test(name))) {
                            cell.setAttribute(name, value);
                        }
                    });
                // INFO [Matt] Don't override a sortable attribute binding.
                if(field && !Array.from(heading.attributes).some(({ name }) => /sortable/i.test(name))) {
                    heading.setAttribute('sortable', `property: ${field}; target: ess-table`);
                    heading.setAttribute('role', 'button');
                    heading.setAttribute('aria-description', EssTableCustomElement.i18n.tr('ui:table.column.sortable'));
                }
                outerHeading.appendChild(heading);
                row.content.appendChild(cell);
                header.content.appendChild(outerHeading);

                // test attributes
                const testName = col.getAttribute(TEST_NAME_ATTRIBUTE);
                let testId: string = '';
                if(testName) {
                    // use test-name= attribute if specified
                    testId = testName;
                }
                else if(rawKey && !INTERPOLATION_RE.test(rawKey)) {
                    // otherwise, use key=, after removing any extra $qualifiers
                    testId = removeTranslationKeyQualifiers(rawKey);
                }
                if(testId) {
                    // set test-id for column and cell
                    outerHeading.setAttribute('test-id', `col-${testId}`);
                    cell.setAttribute('test-id', `cell-${testId}`);
                }

                col.remove();
            });

            el.appendChild(header);
            el.appendChild(row);
        }

        if(details.length > 0) {
            const template = document.createElement('template');
            template.setAttribute('replace-part', 'detail-template');
            details.forEach((detail) => {
                const tr = document.createElement('tr');
                const td = document.createElement('td');
                const detailWrapper = document.createElement('div');

                // add a td placeholder for selectable tables
                if(el.hasAttribute('selectable')) {
                    tr.appendChild(document.createElement('td'));
                }

                // add a td placeholder for the expansion arrow column
                const placeholder = document.createElement('td');
                placeholder.classList.add('ess-col-placeholder');
                tr.appendChild(placeholder);

                // update the detail cell with attributes from the detail templates element
                td.setAttribute('colspan', '${columnCount}');
                Array.from(detail.attributes).forEach(({ name, value }) => td.setAttribute(name, value));
                tr.appendChild(td);

                // fill the detail wrapper with elements from the detail template element
                detailWrapper.classList.add('ess-detail-wrapper');
                Array.from(detail.childNodes).forEach((node) => detailWrapper.appendChild(node));
                td.appendChild(detailWrapper);

                // add expansion awareness to the row
                tr.setAttribute('class', `${TableClass.DetailRow} \${isExpanded(item, expanded.length) | toggle: '${TableClass.ExpandedDetailRow}'}`);
                tr.setAttribute('id', 'detail-section-${$parent.$parent.id}-${$index}');
                template.content.appendChild(tr);

                el.classList.add(TableClass.DetailTable);
                detail.remove();
            });

            el.appendChild(template);
        }

        return true;
    }

    public restoreExpanded(expandedItems: T): void {
        const matcher: (a: T, b: T) => boolean = this.matcher;
        const typeOfData: string = this.data.length > 0 ? typeof this.data[0] : 'undefined';

        if(!(!!expandedItems && Array.isArray(expandedItems) && expandedItems.every((value) => typeof value === typeOfData))) {
            return;
        }

        if(expandedItems !== undefined) {
            expandedItems.forEach((item) => {
                const index = this.data.findIndex((data) => matcher(data, item));
                if(index > -1) {
                    this.open(index);
                }
            });
        }
        this.syncExpandedList();
    }

    public restoreSelected(selectedItems: Array<T>): void {
        const matcher: (a: T, b: T) => boolean = this.matcher;
        const typeOfData: string = this.data.length > 0 ? typeof this.data[0] : 'undefined';

        if(!(!!selectedItems && Array.isArray(selectedItems) && selectedItems.every((value) => typeof value === typeOfData))) {
            return;
        }

        selectedItems.forEach((item) => {
            const index = this.data.findIndex((data) => matcher(data, item));  //Watt: Can probably remove this check, since it seems to be done inside toggleInArray()
            if(index > -1) {
                const selected = this.toggleInArray(item, this.selected, true) === true;
            }
        });

        this.syncSelectedList();
    }
}
